import { Environment } from 'src/Environment';
import { v4 as generateUUID } from 'uuid';
import { request } from '../../../services/request';

const authHeaders = {
  Origin: `${Environment.current?.uiHost}`,
  'Sec-Fetch-Site': 'cross-site',
  'Sec-Fetch-Mode': 'no-cors',
  'Sec-Fetch-Dest': 'empty',
  Referer: `${Environment.current?.uiHost}`,
};

export type CreateOrderResult = {
  orderNumber: string;
};

export type ReserveFundsResult = {
  status: string;
  statusMessage: string;
  orderNumber: string;
  origin: string;
  channel: string;
};

export interface IPaymentGatewayService {
  createOrder(
    mobileNumber: string,
    idNumber: string,
    totalToBePaid: number,
    abortController?: AbortController,
  ): Promise<CreateOrderResult>;

  reserveFunds(orderId: string, abortController?: AbortController): Promise<void>;
}

export class PaymentGatewayService implements IPaymentGatewayService {
  private baseUrl: string;

  // constants - URLS
  private ORDERS_ENDPOINT_PATH = 'onboarding/orders';
  private GET_TRANSACTION_DETAILS_PATH = 'payments/web/vodapay-onboarding/payment/credit/details';

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  createOrder(
    mobileNumber: string,
    idNumber: string,
    totalToBePaid: number,
    abortController?: AbortController,
  ): Promise<CreateOrderResult> {
    const body = {
      relatedParty: [
        {
          id: 'ZAONBRDNG',
          name: 'SSO_DID',
          role: 'Customer',
          '@referredType': 'INDIVIDUAL',
        },
      ],
      description: 'SMME Onboarding',
      requestedCompletionDate: new Date(),
      notificationContact: mobileNumber,
      did: 'ZAONBRDNG',
      channel: {
        id: 'ONLINE',
        name: 'www.vodacom.co.za',
      },
      payment: {
        id: 'VisaMasterCard',
        name: 'VisaMasterCard',
      },
      id: generateUUID(),
      orderItem: [
        {
          id: generateUUID(),
          itemPrice: [
            //leave as is - maybe we can use this to specify what products were purchased, but it is uncertain
            {
              description: 'onboarding',
              name: 'onboarding',
              price: {
                taxIncludedAmount: {
                  unit: 'ZAR',
                  value: 0,
                },
                taxRate: 0,
              },
              unitOfMeasure: 'string',
            },
          ],
          product: {
            id: generateUUID(),
            name: 'onboarding',
            charateristics: [
              {
                name: 'vasAccountNumber',
                value: idNumber,
              },
              {
                name: 'minAmount',
                value: 0,
              },
              {
                name: 'maxAmount',
                value: 0,
              },
              {
                name: 'bsstDue',
                value: 0,
              },
              {
                name: 'msisdn',
                value: mobileNumber,
              },
              {
                name: 'purchaseAmount',
                value: totalToBePaid,
              },
            ],
            productSpecification: {
              id: '7',
              name: 'VODAPAY_ONBOARDING',
            },
          },
        },
      ],
    };

    return request(
      this.ORDERS_ENDPOINT_PATH,
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify(body),
          headers: authHeaders,
          signal: abortController?.signal || null,
        },
      },
      Environment.current?.orderManagementApiBaseURL,
      null,
      false,
      true,
    ).then((response) => {
      if (response?.status !== 200 && !abortController?.signal?.aborted) {
        throw new Error(
          `PGW revamp: error response from the payment gateway ${this.ORDERS_ENDPOINT_PATH} POST endpoint: response status: ${response.status}`,
        );
      }

      const orderNumber = response.result[0].orderNo;
      return {
        orderNumber: orderNumber,
      };
    });
  }

  reserveFunds(orderId: string, abortController?: AbortController): Promise<void> {
    return this.getTransactionDetails(orderId, abortController).then((response) => {
      return this.redirectToPaymentGateway(response);
    });
  }

  redirectToPaymentGateway(transactionDetails) {
    let postParams = transactionDetails.formInputFields;

    let paymentGatewayForm = document.createElement('FORM'); // @ts-ignore
    paymentGatewayForm.method = 'post'; // @ts-ignore
    paymentGatewayForm.name = 'ucf'; // @ts-ignore
    paymentGatewayForm.id = 'ucf'; // @ts-ignore
    paymentGatewayForm.type = 'HIDDEN'; // @ts-ignore
    paymentGatewayForm.action = transactionDetails.formAttributes.action; // @ts-ignore
    paymentGatewayForm.target = '_self';

    for (let i = 0; i < postParams.length; i++) {
      let inputElement = document.createElement('INPUT'); // @ts-ignore
      inputElement.type = postParams[i].type || 'HIDDEN'; // @ts-ignore
      inputElement.name = postParams[i].name; // @ts-ignore
      inputElement.id = postParams[i].id; // @ts-ignore
      inputElement.value = postParams[i].value;
      paymentGatewayForm.appendChild(inputElement);
    }

    document.body.appendChild(paymentGatewayForm);
    // @ts-ignore
    paymentGatewayForm.submit();
  }

  getTransactionDetails(orderId, abortController?: AbortController) {
    return request(
      `${this.GET_TRANSACTION_DETAILS_PATH}?orderId=${orderId}`,
      {
        fetchOptions: {
          method: 'GET',
          signal: abortController?.signal || null,
        },
      },
      this.baseUrl,
    ).then((response) => {
      if (response?.status !== 200 && !abortController?.signal?.aborted) {
        throw new Error(
          `Transaction details revamp: error response from the payment gateway ${this.GET_TRANSACTION_DETAILS_PATH} GET endpoint, response status: ${response.status}`,
        );
      }

      return response.result;
    });
  }
}
